<template>
  <PageHeader :showBreadcrumb="true" />

  <div class="container" v-if="!loading">
    <h2 class="page-title mb-6">Check Status</h2>

    <TabView v-model:activeIndex="activeTabIndex">
      <!-- Appeal Status Tab -->
      <TabPanel header="Appeal Status">
        <form @submit.stop.prevent="onSearchAppeal">

          <div class="form-group">
            <label>Email Address</label>
            <InputText v-model="appeal.email" placeholder="Email Address" class="input-field" />
          </div>

          <div class="form-group">
            <label>Patient Last Name</label>
            <InputText v-model="appeal.lastName" placeholder="Last Name" class="input-field" />
          </div>

          <div class="form-group">
            <label>Appeal ID</label>
            <InputText v-model="appeal.appealId" placeholder="Appeal ID" class="input-field" />
          </div>

          <div class="button-wrapper">
            <Button type="submit" label="Search Appeal" icon="pi pi-search" />
          </div>

          <div v-if="appeal.error" class="error-message">
            {{ appeal.error }}
          </div>

          <div v-if="appeal.appealStatus" class="result-card mt-6">
            <h3 class="result-title">Appeal Status Results</h3>
            <div class="result-row"><strong>Status:</strong> {{ appeal.appealStatus }}</div>
            <div class="result-row"><strong>Appeal Outcome:</strong> {{ appeal.appealOutcome }}</div>
            <div class="result-row"><strong>Date Sent to IRO:</strong> {{ appeal.iroAt }}</div>
          </div>

        </form>
      </TabPanel>

      <!-- Complaint Status Tab -->
      <TabPanel header="Complaint Status">
        <form @submit.stop.prevent="onSearchComplaint">

          <div class="form-group">
            <label>Email Address</label>
            <InputText v-model="complaint.email" placeholder="Email Address" class="input-field" />
          </div>

          <div class="form-group">
            <label>Last Name</label>
            <InputText v-model="complaint.lastName" placeholder="Last Name" class="input-field" />
          </div>

          <div class="form-group">
            <label>Complaint ID</label>
            <InputText v-model="complaint.complaintId" placeholder="Complaint ID" class="input-field" />
          </div>

          <div class="button-wrapper">
            <Button type="submit" label="Search Complaint" icon="pi pi-search" />
          </div>

          <div v-if="complaint.error" class="error-message">
            {{ complaint.error }}
          </div>

          <div v-if="complaint.complaintStatus" class="result-card mt-6">
            <h3 class="result-title">Complaint Status Results</h3>
            <div class="result-row"><strong>Status:</strong> {{ complaint.complaintStatus }}</div>
            <div class="result-row"><strong>Outcome:</strong> {{ complaint.complaintOutcome }}</div>
          </div>

        </form>
      </TabPanel>
    </TabView>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue';
import PageHeader from '@/components/common/PageHeader.vue';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';

import { appealStatusCheck } from '@/services/externalAppealService.js';
import { complaintStatusCheck } from '@/services/consumerComplaintService.js';
import { AppealStatusSearchResponseModel } from '@/models/AppealStatusSearchResponseModel.js';
import { ComplaintStatusSearchResponseModel } from '@/models/ComplaintStatusSearchResponseModel.js';
import { AppealStatusSearchModel } from '@/models/AppealStatusSearchModel.js';
import { ComplaintStatusSearchModel } from '@/models/ComplaintStatusSearchModel.js';

const activeTabIndex = ref(0); // 0 = Appeal, 1 = Complaint
const loading = ref(false);

const appeal = reactive(new AppealStatusSearchModel());
const complaint = reactive(new ComplaintStatusSearchModel());

// Appeal Search
const onSearchAppeal = async () => {
  activeTabIndex.value = 0;

  appeal.error = '';
  appeal.appealStatus = null;
  appeal.appealOutcome = null;
  appeal.iroAt = null;

  if (!appeal.email || !appeal.lastName || !appeal.appealId) {
    appeal.error = 'Email, Last Name, and Appeal ID are required.';
    return;
  }

  try {
    loading.value = true;

    const request = new AppealStatusSearchResponseModel({});
    const response = await appealStatusCheck({
      email: appeal.email,
      lastName: appeal.lastName,
      appealId: appeal.appealId,
      ...request
    });

    Object.assign(appeal, response.data);

  } catch {
    appeal.error =
        'No appeal found. Please verify your information. Results may take up to three business days.';
  } finally {
    loading.value = false;
  }
};

// Complaint Search
const onSearchComplaint = async () => {
  activeTabIndex.value = 1;

  complaint.error = '';
  complaint.complaintStatus = null;
  complaint.complaintOutcome = null;

  if (!complaint.email || !complaint.lastName || !complaint.complaintId) {
    complaint.error = 'Email, Last Name, and Complaint ID are required.';
    return;
  }

  try {
    loading.value = true;

    const request = new ComplaintStatusSearchResponseModel({});
    const response = await complaintStatusCheck({
      email: complaint.email,
      lastName: complaint.lastName,
      complaintId: complaint.complaintId,
      ...request
    });

    Object.assign(complaint, response.data);

  } catch {
    complaint.error =
        'No complaint found. Please verify your information. Results may take up to three business days.';
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.container {
  max-width: 480px;
  margin: 0 auto;
  padding: 1.5rem;
}

.page-title {
  font-size: 1.75rem;
  font-weight: 600;
  text-align: center;
  color: #111827;
}

.form-group {
  margin-bottom: 1.5rem;
  display: flex;
  flex-direction: column;
}

.form-group label {
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.input-field {
  padding: 0.5rem 0.75rem;
  font-size: 1rem;
  border: 1px solid #d1d5db; /* light gray border */
  border-radius: 0.375rem;
  background-color: #ffffff;
}

.button-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 1rem;
  margin-bottom: 1rem;
}

.error-message {
  color: #dc2626; /* red-600 */
  font-weight: 600;
  text-align: center;
}

.result-card {
  background-color: #f3f4f6;
  border-radius: 0.5rem;
  padding: 1.5rem;
}

.result-title {
  font-size: 1.25rem;
  font-weight: 600;
}

.result-row {
  margin-bottom: 0.75rem;
  font-size: 1rem;
  display: flex;
  gap: 0.5rem;
}
</style>
